import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { api } from '@/services/api'
import {
  CreditCost,
  UserCreditsV2,
  CreateCreditCostRequest,
  UpdateCreditCostRequest,
  SimulateCostChangeRequest,
  SimulateCostChangeResponse,
  AdjustCreditsRequest,
  CreditLimits,
  UpdateCreditLimitsRequest,
} from '@/types'
import { toast } from 'sonner'

const CREDITS_QUERY_KEY = 'credits'

export const useCreditCosts = () => {
  return useQuery<CreditCost[]>({
    queryKey: [CREDITS_QUERY_KEY, 'costs'],
    queryFn: async () => {
      const { data } = await api.get('/credit-costs')
      return data
    },
  })
}

export const useUpdateCreditCost = () => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async ({ id, updates }: { id: string; updates: UpdateCreditCostRequest }) => {
      const { data } = await api.patch(`/credit-costs/${id}`, updates)
      return data
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [CREDITS_QUERY_KEY] })
      toast.success('Costo actualizado')
    },
  })
}

export const useCreateCreditCost = () => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (cost: CreateCreditCostRequest) => {
      const { data } = await api.post('/credit-costs', cost)
      return data
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [CREDITS_QUERY_KEY] })
      toast.success('Costo de credito creado')
    },
  })
}

export const useSimulateCostChange = () => {
  return useMutation<SimulateCostChangeResponse, Error, SimulateCostChangeRequest>({
    mutationFn: async (request) => {
      const { data } = await api.post('/credit-costs/simulate', request)
      return data
    },
  })
}

export const useUserCredits = (userId: string) => {
  return useQuery<UserCreditsV2>({
    queryKey: [CREDITS_QUERY_KEY, 'user', userId],
    queryFn: async () => {
      const { data } = await api.get(`/credit-costs/users/${userId}`)
      return data
    },
    enabled: !!userId,
  })
}

export const useAdjustCredits = () => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async ({ userId, request }: { userId: string; request: AdjustCreditsRequest }) => {
      const { data } = await api.post(`/credit-costs/users/${userId}/adjust`, request)
      return data
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [CREDITS_QUERY_KEY] })
      toast.success('Creditos ajustados')
    },
  })
}

export const useCreditLimits = () => {
  return useQuery<CreditLimits>({
    queryKey: [CREDITS_QUERY_KEY, 'limits'],
    queryFn: async () => {
      const { data } = await api.get('/credit-limits')
      return data
    },
  })
}

export const useUpdateCreditLimits = () => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (request: UpdateCreditLimitsRequest) => {
      const { data } = await api.put('/credit-limits', request)
      return data
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [CREDITS_QUERY_KEY, 'limits'] })
      toast.success('Limites de creditos actualizados')
    },
  })
}
